import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Card,
  CardContent,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import api , { API_BASE_URL } from "../../utils/api";

const steps = ["Request Info", "Details & Files", "Review & Submit"];

const AddArchitecturalRequestSmart = ({ propertyId, accountId }) => {
  const [activeStep, setActiveStep] = useState(0);

  const [requestTypes, setRequestTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);

  const [formData, setFormData] = useState(() => {
    const draft = localStorage.getItem("arch_request_draft");
    return draft
      ? JSON.parse(draft)
      : {
          description: "",
          request_type: "",
          subtype: "",
          property_id: propertyId || "",
          account_id: accountId || "",
          comment: "",
        };
  });

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // 🧠 Save draft on change
  useEffect(() => {
    localStorage.setItem("arch_request_draft", JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const res = await api.get(`/architectural-request-types`);
        setRequestTypes(res.data);
      } catch (err) {
        console.error("Failed to fetch types:", err);
      }
    };
    fetchTypes();
  }, []);

  const handleFormChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "request_type") {
      try {
        const res = await api.get(`/architectural-request-subtypes`, {
          params: { typeId: value },
        });
        setSubtypes(res.data);
        setFormData((prev) => ({ ...prev, subtype: "" }));
      } catch (err) {
        console.error("Subtypes error:", err);
        setSubtypes([]);
      }
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    setSelectedFiles((prev) => [...prev, ...acceptedFiles]);
  }, []);

  const removeFile = (i) => {
    setSelectedFiles((prev) => prev.filter((_, index) => index !== i));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: "image/*,.pdf,.doc,.docx",
    multiple: true,
  });

  const nextStep = () => setActiveStep((s) => s + 1);
  const prevStep = () => setActiveStep((s) => s - 1);

  const handleSubmit = async () => {
  try {
    const res = await api.post(`/architectural-requests`, formData);
    const request_id = res.data?.id;

    if (!request_id) {
      throw new Error("Missing request_id from architectural request response");
    }

    if (selectedFiles.length > 0) {
      const uploadData = new FormData();
      uploadData.append("request_id", request_id);

      selectedFiles.forEach((file) => {
        uploadData.append("upload_urls", file);
      });

      await api.post(`/request-files`, uploadData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    }

    // ✅ Reset form
    setFormData({
      description: "",
      request_type: "",
      subtype_id: "",
      property_id: propertyId,
      account_id: accountId,
      comment: "",
    });
    setSelectedFiles([]);
    setActiveStep(0);
    localStorage.removeItem("arch_request_draft");

    // ✅ Show success message
    setSnackbar({
      open: true,
      message: "✅ Architectural Request submitted successfully!",
      severity: "success",
    });

  } catch (err) {
    console.error("❌ Submit Error:", err);
    setSnackbar({
      open: true,
      message: err.response?.data?.message || "Submission failed",
      severity: "error",
    });
  }
};

  

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
  <Tooltip title="Choose the type of request you're submitting">
    <InputLabel sx={{ backgroundColor: "white", px: 0.5 }}>Request Type</InputLabel>
  </Tooltip>
  <Select
    name="request_type"
    value={formData.request_type}
    onChange={handleFormChange}
    required
    sx={{ py: 1.5, fontSize: 14 }} // added vertical padding and font sizing
  >
    {requestTypes.map((type) => (
      <MenuItem key={type.id} value={type.id}>
        {type.type}
      </MenuItem>
    ))}
  </Select>
</FormControl>

<FormControl fullWidth sx={{ mb: 2 }} disabled={!subtypes.length}>
  <InputLabel sx={{ backgroundColor: "white", px: 0.5 }}>Subtype</InputLabel>
  <Select
    name="subtype_id"
    value={formData.subtype_id}
    onChange={handleFormChange}
    required
    sx={{ py: 1.5, fontSize: 14 }}
  >
    {subtypes.map((sub) => (
      <MenuItem key={sub.subtype_id} value={sub.subtype_id}>
        {sub.subtype_name}
      </MenuItem>
    ))}
  </Select>
</FormControl>

          </>
        );
      case 1:
        return (
          <>
            <Tooltip title="Provide a detailed explanation of your request">
              <TextField fullWidth label="Description" name="description" value={formData.description} onChange={handleFormChange} multiline rows={3} sx={{ mb: 2 }} required />
            </Tooltip>
            <TextField fullWidth label="Comment" name="comment" value={formData.comment} onChange={handleFormChange} multiline rows={2} sx={{ mb: 2 }} />

            <Box {...getRootProps()} sx={{
              border: "2px dashed #ccc", borderRadius: "8px", p: 3, mb: 2, cursor: "pointer", textAlign: "center",
              backgroundColor: isDragActive ? "#f9f9f9" : "#fafafa",
            }}>
              <input {...getInputProps()} />
              <Typography variant="body2">
                {isDragActive ? "Drop files here..." : "Drag & drop or click to upload files"}
              </Typography>
            </Box>

            {selectedFiles.map((file, i) => (
              <Box key={i} display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="body2">{file.name}</Typography>
                <IconButton color="error" onClick={() => removeFile(i)}><DeleteIcon /></IconButton>
              </Box>
            ))}
          </>
        );
      case 2:
        return (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6">Review Your Request</Typography>
              <Typography><b>Type:</b> {requestTypes.find(t => t.id === formData.request_type)?.type}</Typography>
              <Typography><b>Subtype:</b> {subtypes.find(s => s.subtype_id === formData.subtype)?.subtype_name}</Typography>
              <Typography><b>Description:</b> {formData.description}</Typography>
              <Typography><b>Comment:</b> {formData.comment}</Typography>
              <Typography><b>Files:</b> {selectedFiles.map(f => f.name).join(", ") || "None"}</Typography>
            </CardContent>
          </Card>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", p: 4 }}>
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      {renderStepContent()}

      <Box mt={4} display="flex" justifyContent="space-between">
        {activeStep > 0 && <Button onClick={prevStep}>Back</Button>}
        {activeStep < steps.length - 1 ? (
          <Button variant="contained" onClick={nextStep}>Next</Button>
        ) : (
          <Button variant="contained" color="primary" onClick={handleSubmit}>Submit Request</Button>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AddArchitecturalRequestSmart;
